import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import api from "../utils/api";
import SupplierForm from "../components/SupplierForm";
import { formatUkDate } from "../utils/dateLocale";

const SupplierLists = () => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openForm, setOpenForm] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const response = await api.get("/api/suppliers");
      setSuppliers(response.data.suppliers || []);
      setError("");
    } catch (err) {
      console.error("Failed to fetch suppliers:", err);
      setError(err.response?.data?.error || "Failed to load suppliers");
      setSuppliers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this supplier?"))
      return;
    try {
      await api.delete(`/api/suppliers/${id}`);
      fetchSuppliers();
    } catch (err) {
      console.error("Failed to delete supplier:", err);
      setError(err.response?.data?.error || "Failed to delete supplier");
    }
  };

  const handleEdit = (row) => {
    setEditingSupplier(row);
    setOpenForm(true);
  };

  const handleFormClose = () => {
    setOpenForm(false);
    setEditingSupplier(null);
  };

  const handleFormSubmit = async (payload) => {
    try {
      if (editingSupplier) {
        await api.put(`/api/suppliers/${editingSupplier.id}`, payload);
      } else {
        await api.post("/api/suppliers", payload);
      }
      handleFormClose();
      fetchSuppliers();
    } catch (err) {
      console.error("Failed to save supplier:", err);
      throw err;
    }
  };

  const columns = [
    { field: "status", headerName: "Status", width: 100, minWidth: 90 },
    {
      field: "typeOfServiceProvided",
      headerName: "Type of service",
      flex: 1,
      minWidth: 160,
    },
    {
      field: "supplierCompany",
      headerName: "Supplier Company",
      flex: 1.2,
      minWidth: 180,
    },
    {
      field: "serviceDescription",
      headerName: "Service description",
      flex: 1.5,
      minWidth: 200,
    },
    {
      field: "billingType",
      headerName: "Billing Type",
      width: 130,
      minWidth: 110,
    },
    {
      field: "contractEnds",
      headerName: "Contract Ends",
      width: 130,
      minWidth: 120,
      valueFormatter: (params) =>
        params?.value != null ? formatUkDate(params.value) : "",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => (
        <Box display="flex" gap={0.5}>
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleEdit(params.row)}
            aria-label="Edit"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDelete(params.row.id)}
            aria-label="Delete"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box
      display="flex"
      flexDirection="column"
      width="100%"
      height={{ xs: "auto", md: "calc(100dvh - 112px)" }}
      minHeight={{ xs: 480, md: 0 }}
      gap={2}
    >
      <Box display="flex" justifyContent="flex-end" alignItems="center" flexShrink={0}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditingSupplier(null);
            setOpenForm(true);
          }}
        >
          Add Supplier
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ flexShrink: 0 }}>
          {error}
        </Alert>
      )}

      <Card
        sx={{
          width: "100%",
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <CardContent
          sx={{
            width: "100%",
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            pt: 2,
            "&:last-child": { pb: 2 },
          }}
        >
          <Box flex={1} minHeight={0} width="100%">
            <DataGrid
              rows={suppliers}
              columns={columns}
              loading={loading}
              getRowId={(row) => row.id}
              disableSelectionOnClick
              disableColumnMenu
              pageSizeOptions={[10, 25, 50]}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
              }}
              sx={{
                height: "100%",
                width: "100%",
                border: 0,
                "& .MuiDataGrid-main": { width: "100%" },
                "& .MuiDataGrid-virtualScroller": { width: "100%" },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      <Dialog
        open={openForm}
        onClose={handleFormClose}
        maxWidth={fullScreen ? false : "md"}
        fullWidth
        fullScreen={fullScreen}
        PaperProps={{
          style: {
            maxHeight: fullScreen ? "100%" : "90vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle>
          {editingSupplier ? "Edit Supplier" : "Add New Supplier"}
        </DialogTitle>
        <DialogContent
          style={{
            flex: "1 1 auto",
            minHeight: 0,
            overflow: "auto",
          }}
        >
          <SupplierForm
            supplier={editingSupplier}
            onSubmit={handleFormSubmit}
            onCancel={handleFormClose}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default SupplierLists;
